import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { CaseDetail } from '@/data/caseDetails';
import { Button } from './ui/button';

interface CaseDisplayProps {
  caseDetail: CaseDetail;
  onBack: () => void;
}

const StatusBadge = ({ type, text }: { type: string; text: string }) => {
  const colors = {
    ineligible: 'bg-red-100 text-red-800 border-red-200',
    eligible: 'bg-green-100 text-green-800 border-green-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    guidance: 'bg-blue-100 text-blue-800 border-blue-200',
    innovation: 'bg-purple-100 text-purple-800 border-purple-200'
  };

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${colors[type as keyof typeof colors]}`}>
      {text}
    </span>
  );
};

const PatentCard = ({ title, numbers, description, claims }: any) => (
  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
    <h4 className="font-semibold text-gray-800 mb-2">{title}</h4>
    <div className="text-sm text-gray-600 mb-2"><strong>Patents:</strong> {numbers}</div>
    <p className="text-sm text-gray-700 mb-3">{description}</p>
    <div className="space-y-2">
      {claims.map((claim: string, idx: number) => (
        <div key={idx} className="flex items-start gap-2">
          <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
          <span className="text-sm text-gray-600">{claim}</span>
        </div>
      ))}
    </div>
  </div>
);

export function CaseDisplay({ caseDetail, onBack }: CaseDisplayProps) {
  const [expandedSections, setExpandedSections] = useState(new Set(['overview']));

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  return (
    <div className="max-w-6xl mx-auto">
      <Button
        onClick={onBack}
        variant="outline"
        className="mb-6"
      >
        ← Back to Court Precedents
      </Button>

      <div className="border-b border-gray-200 pb-6 mb-6">
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{caseDetail.title}</h1>
          <p className="text-gray-600">{caseDetail.courtInfo.subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {caseDetail.badges.map((badge, i) => (
            <StatusBadge key={i} type={badge.type} text={badge.text} />
          ))}
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-600">
            <strong>Case Citation:</strong> {caseDetail.courtInfo.citation}
          </div>
          {caseDetail.pdfLink && (
            <div className="mt-2">
              <a
                href={caseDetail.pdfLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-amber-600 hover:text-amber-700 underline"
              >
                View Full Opinion (PDF) ↗
              </a>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {caseDetail.sections.map((section) => {
          const IconComponent = section.icon;
          return (
            <div key={section.id} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-all duration-200 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="text-gray-600">
                    {IconComponent && <IconComponent className="w-5 h-5" />}
                  </div>
                  <h2 className="text-lg font-semibold text-gray-800">{section.title}</h2>
                </div>
                {expandedSections.has(section.id) ? (
                  <ChevronDown className="w-5 h-5 text-gray-600" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                )}
              </button>
              {expandedSections.has(section.id) && (
                <div className="px-6 py-4 border-t border-gray-200 bg-white">
                  {section.contentType === 'html' && section.content}
                  {section.contentType === 'quotes' && (
                    <div className="space-y-3">
                      {(section.content as string[]).map((quote, idx) => (
                        <div key={idx} className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r">
                          <span className="text-sm text-gray-700 italic">{quote}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {section.contentType === 'patents' && (
                    <div className="space-y-4">
                      {(section.content as any[]).map((patent, i) => (
                        <PatentCard key={i} {...patent} />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
